import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

type Props = { projectId?: string };

export const WorkFrontsPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const frontsQ = useQuery({
    queryKey: ['cep-work-fronts', projectId],
    queryFn: () => epcService.getWorkFronts(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const areasQ = useQuery({
    queryKey: ['cep-areas', projectId],
    queryFn: () => epcService.getWorkAreas(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createWorkFront({ ...values, projectId }),
    onSuccess: () => {
      message.success('Work front created');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const refreshMut = useMutation({
    mutationFn: (id: string) => epcService.refreshWorkFront(id),
    onSuccess: () => {
      message.success('Readiness refreshed');
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for Work Front readiness</Tag>;

  const rows = (frontsQ.data as any[]) || [];
  const areas = (areasQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          New Work Front
        </Button>
        <Tag color="geekblue">CEP Wave 2</Tag>
        <Tag>If no fronts exist, areas with packages appear as virtual fronts</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={frontsQ.isLoading}
        dataSource={rows}
        columns={[
          {
            title: 'Code',
            dataIndex: 'code',
            width: 140,
            render: (v: string, r: any) => (
              <Space>
                {v}
                {r.virtual ? <Tag>virtual</Tag> : null}
              </Space>
            ),
          },
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          {
            title: 'Readiness',
            dataIndex: 'readiness_status',
            width: 160,
            render: (v: string) => (
              <Tag color={v === 'Ready_to_Build' ? 'green' : 'orange'}>{v || '—'}</Tag>
            ),
          },
          {
            title: 'Score',
            dataIndex: 'readiness_score',
            width: 80,
            render: (v: unknown) => String(v ?? 0),
          },
          { title: 'Packages', dataIndex: 'package_count', width: 90 },
          {
            title: 'Reasons',
            dataIndex: 'readiness_reasons',
            ellipsis: true,
            render: (v: string[] | undefined) => (v || []).join('; ') || '—',
          },
          {
            title: '',
            width: 110,
            render: (_: unknown, r: any) =>
              r.virtual ? null : (
                <Button
                  size="small"
                  loading={refreshMut.isPending}
                  onClick={() => refreshMut.mutate(String(r.id))}
                >
                  Refresh
                </Button>
              ),
          },
        ]}
      />
      <Modal
        title="New Work Front"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => createMut.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="constructionAreaId" label="Construction area">
            <Select
              allowClear
              options={areas.map((a: any) => ({
                value: String(a.id),
                label: `${a.code} — ${a.title}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkFrontsPanel;
